import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Template } from '../_types';
import { useTemplate } from '../_data_sources/useTemplate';

interface TemplateDetailsContextValue {
  template: Template | null;
  parentTemplate: Template | null;
  isLoading: boolean;
  isError: boolean;
  error: any;
  isChildTemplate: boolean;
  isDraft: boolean;
}

export const TemplateDetailsContext = createContext<TemplateDetailsContextValue>({} as TemplateDetailsContextValue);

export const TemplateDetailsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  // Read isDraft parameter from URL
  const isDraft = searchParams.get('isDraft') === 'true';

  // Fetch the main template
  const templateQuery = useTemplate(
    Number(id),
    {},
    (error) => {
      console.error('Error fetching template:', error);
    },
    isDraft
  );

  const template = templateQuery.data ?? null;
  const isChildTemplate = !!template?.parentTemplateId;

  // Conditionally fetch parent template if this is a child template
  // Note: Agency templates don't have parent template links in their UI
  const parentTemplateQuery = useTemplate(
    template?.parentTemplateId || 0,
    { enabled: isChildTemplate && !!template?.parentTemplateId },
    (error) => {
      console.error('Error fetching parent template:', error);
    }
  );

  const value: TemplateDetailsContextValue = useMemo(
    () => ({
      template,
      parentTemplate: parentTemplateQuery.data ?? null,
      isLoading: templateQuery.isFetching,
      isError: templateQuery.isError,
      error: templateQuery.isError ? templateQuery : null,
      isChildTemplate,
      isDraft,
    }),
    [
      template,
      parentTemplateQuery.data,
      templateQuery.isFetching,
      templateQuery.isError,
      isChildTemplate,
      isDraft,
    ]
  );

  return <TemplateDetailsContext.Provider value={value}>{children}</TemplateDetailsContext.Provider>;
};

export const useTemplateDetailsContext = () => useContext(TemplateDetailsContext);
